/**
 * Date formatting using the device locale and local timezone (no fixed IANA zone).
 * Times use 12-hour clock with AM/PM for readability.
 */

const LOCAL_DATE_TIME_OPTS: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: true,
};

const LOCAL_DATE_OPTS: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
};

const LOCAL_FULL_DATE_OPTS: Intl.DateTimeFormatOptions = {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
};

const LOCAL_DATE_TIME_NO_SEC_OPTS: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: true,
};

/**
 * Start of a local calendar day as UTC ISO 8601 (for API range filters).
 * `ymd` must be `YYYY-MM-DD` from a local date control (browser date input).
 */
export function localCalendarYmdStartUtcIso(ymd: string): string {
  const [y, m, d] = ymd.split('-').map((p) => parseInt(p, 10));
  if (
    ymd.length !== 10 ||
    ymd[4] !== '-' ||
    ymd[7] !== '-' ||
    [y, m, d].some((n) => Number.isNaN(n))
  ) {
    throw new Error('Invalid YYYY-MM-DD');
  }
  return new Date(y, m - 1, d, 0, 0, 0, 0).toISOString();
}

/**
 * End of a local calendar day as UTC ISO 8601 (for API range filters).
 */
export function localCalendarYmdEndUtcIso(ymd: string): string {
  const [y, m, d] = ymd.split('-').map((p) => parseInt(p, 10));
  if (
    ymd.length !== 10 ||
    ymd[4] !== '-' ||
    ymd[7] !== '-' ||
    [y, m, d].some((n) => Number.isNaN(n))
  ) {
    throw new Error('Invalid YYYY-MM-DD');
  }
  return new Date(y, m - 1, d, 23, 59, 59, 999).toISOString();
}

/**
 * Format a date with full timestamp in the given locale (or runtime default) and local timezone.
 * @param locale BCP 47 tag(s), e.g. from Angular `LOCALE_ID` (`en-AU` in shipped apps).
 */
export function formatDate(
  date: Date | string | undefined | null,
  locale?: string | string[],
): string {
  if (!date) {
    return 'N/A';
  }

  try {
    const d = typeof date === 'string' ? new Date(date) : date;

    if (isNaN(d.getTime())) {
      return 'N/A';
    }

    return d.toLocaleString(locale ?? undefined, LOCAL_DATE_TIME_OPTS);
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'N/A';
  }
}

/**
 * Format a date (date only) in the given locale (or runtime default) and local timezone.
 */
export function formatDateOnly(
  date: Date | string | undefined | null,
  locale?: string | string[],
): string {
  if (!date) {
    return 'N/A';
  }

  try {
    const d = typeof date === 'string' ? new Date(date) : date;

    if (isNaN(d.getTime())) {
      return 'N/A';
    }

    return d.toLocaleDateString(locale ?? undefined, LOCAL_DATE_OPTS);
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'N/A';
  }
}

/**
 * Format a date with a full calendar label in the given locale (or runtime default) and local timezone.
 */
export function formatDateLong(
  date: Date | string | undefined | null,
  locale?: string | string[],
): string {
  if (!date) {
    return 'N/A';
  }

  try {
    const d = typeof date === 'string' ? new Date(date) : date;

    if (isNaN(d.getTime())) {
      return 'N/A';
    }

    return d.toLocaleDateString(locale ?? undefined, LOCAL_FULL_DATE_OPTS);
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'N/A';
  }
}

/**
 * Format a date with date and time (no seconds) in the given locale (or runtime default) and local timezone.
 */
export function formatDateTime(
  date: Date | string | undefined | null,
  locale?: string | string[],
): string {
  if (!date) {
    return 'N/A';
  }

  try {
    const d = typeof date === 'string' ? new Date(date) : date;

    if (isNaN(d.getTime())) {
      return 'N/A';
    }

    return d.toLocaleString(locale ?? undefined, LOCAL_DATE_TIME_NO_SEC_OPTS);
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'N/A';
  }
}

/** Max length for display: "99h 59m" (8 chars) or "59m 59s" (7 chars). Values ≥100h show "100h+". */
const DURATION_100H_MS = 100 * 3600 * 1000;
const ONE_HOUR_MS = 3600 * 1000;

/**
 * Format a duration range (median bucket) for time-stats display.
 * Returns bucket range like "0–1 min", "1–5 min", "5–15 min", "≥1 hr".
 * highMs null = last bucket [1hr, inf).
 */
export function formatDurationRange(
  lowMs: number | null | undefined,
  highMs: number | null | undefined,
): string {
  if (lowMs == null || (typeof lowMs === 'number' && isNaN(lowMs))) return '—';
  if (highMs == null || highMs === undefined) {
    if (lowMs >= ONE_HOUR_MS) return '≥1 hr';
    return '—';
  }
  const lowMin = lowMs / 60_000;
  const highMin = highMs / 60_000;
  if (highMs >= ONE_HOUR_MS) {
    if (lowMs >= ONE_HOUR_MS) {
      const lowH = Math.floor(lowMs / ONE_HOUR_MS);
      return lowH >= 1 ? `≥${lowH} hr` : '≥1 hr';
    }
    return `${Math.round(lowMin)}–60 min`;
  }
  if (lowMin < 1 && highMin < 1) {
    const lowSec = Math.floor(lowMs / 1000);
    const highSec = Math.floor(highMs / 1000);
    return lowSec === 0 ? `0–${highSec}s` : `${lowSec}–${highSec}s`;
  }
  return `${Math.round(lowMin)}–${Math.round(highMin)} min`;
}

/**
 * Format overflow bucket (last bucket [1hr, inf)) for display.
 * Returns "60 min+", "1 hr+", etc. Use omitPlusForAtLeast to drop the "+" when redundant.
 */
export function formatOverflowBucket(
  lowMs: number | null | undefined,
  omitPlusForAtLeast = false,
): string {
  if (lowMs == null || (typeof lowMs === 'number' && isNaN(lowMs))) return '—';
  const suffix = omitPlusForAtLeast ? '' : '+';
  if (lowMs >= ONE_HOUR_MS) {
    const h = Math.floor(lowMs / ONE_HOUR_MS);
    return h >= 1 ? `${h} hr${suffix}` : `1 hr${suffix}`;
  }
  const min = Math.round(lowMs / 60_000);
  return `${min} min${suffix}`;
}

/** Metric type for percentile wording. waitTime uses "served/waited"; service/turnaround use "completed/took". */
export type TimeStatMetricKey = 'waitTime' | 'serviceTime' | 'turnaroundTime';

/**
 * Percentile label and value for time-stats. When highMs is null (overflow bucket),
 * uses "waited at least" or "took at least" depending on metric.
 * waitTime: "served within" / "waited at least"
 * serviceTime, turnaroundTime: "completed within" / "took at least"
 */
export function formatPercentileDisplay(
  percentile: number,
  lowMs: number | null | undefined,
  highMs: number | null | undefined,
  metric: TimeStatMetricKey = 'waitTime',
): { prefix: string; value: string } {
  const pct = Math.round(percentile * 100);
  const isOverflow = highMs == null || highMs === undefined;
  const isWaitTime = metric === 'waitTime';
  const withinPhrase = isWaitTime ? 'served within' : 'completed within';
  const atLeastPhrase = isWaitTime ? 'waited at least' : 'took at least';
  const prefix = isOverflow ? `${pct}% ${atLeastPhrase}` : `${pct}% ${withinPhrase}`;
  const value =
    lowMs == null
      ? '—'
      : isOverflow
        ? formatOverflowBucket(lowMs, true) // omit "+" when prefix is "waited/took at least" (redundant)
        : formatDurationRange(lowMs, highMs ?? undefined);
  return { prefix, value };
}

/**
 * Format duration for overflow bucket (compact, e.g. for median badge).
 * Returns "60+ min", "1+ hr", etc.
 */
export function formatOverflowCompact(lowMs: number | null | undefined): string {
  if (lowMs == null || (typeof lowMs === 'number' && isNaN(lowMs))) return '—';
  if (lowMs >= ONE_HOUR_MS) {
    const h = Math.floor(lowMs / ONE_HOUR_MS);
    return h >= 1 ? `${h}+ hr` : '1+ hr';
  }
  const min = Math.round(lowMs / 60_000);
  return `${min}+ min`;
}

export type FormatDurationOptions = {
  /**
   * Live ticking timers: always show seconds (e.g. "5m 03s", "1h 0m 12s").
   * Default stats formatting omits seconds when hours > 0, and hides "0s" when whole minutes.
   */
  live?: boolean;
};

/**
 * Format a duration in milliseconds for time-stats display.
 * Under 1h: "Xm Ys" (e.g. "59m 59s"); 1h to <100h: "Xh Ym" (e.g. "99h 59m"); ≥100h: "100h+".
 * With `{ live: true }`, always includes a seconds segment for running clocks.
 * Live clocks omit a numeric value until at least 1s has elapsed (shows "—" below that).
 */
export function formatDuration(
  ms: number | null | undefined,
  options?: FormatDurationOptions,
): string {
  if (ms == null || (typeof ms === 'number' && isNaN(ms))) return '—';
  const live = options?.live === true;
  if (live && ms < 1000) return '—';
  if (ms <= 0) return '0s';
  if (ms < 1000) return '< 1s';
  const totalSeconds = Math.floor(ms / 1000);
  if (ms >= DURATION_100H_MS) return '100h+';
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) {
    return live ? `${hours}h ${minutes}m ${seconds}s` : `${hours}h ${minutes}m`;
  }
  if (minutes > 0) {
    if (live) return `${minutes}m ${seconds}s`;
    return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
  }
  return `${seconds}s`;
}

/** Full d/h/m/s breakdown for tooltips and “actual” text when the primary label is capped at 100h+. */
function formatDurationDayHourMinuteSecond(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (days > 0) {
    return `${days}d ${hours}h ${minutes}m ${seconds}s`;
  }
  if (hours > 0) {
    return `${hours}h ${minutes}m ${seconds}s`;
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
}

/**
 * Same as {@link formatDuration} for durations under the 100h cap. For ≥100h (shown as `100h+`),
 * returns `100h+ (Nd Xh Ym Zs)` with the full actual duration in parentheses.
 */
export function formatDurationWithActualBeyond100h(
  ms: number | null | undefined,
  options?: FormatDurationOptions,
): string {
  if (ms == null || (typeof ms === 'number' && isNaN(ms))) return '—';
  if (ms >= DURATION_100H_MS) {
    return `100h+ (${formatDurationDayHourMinuteSecond(ms)})`;
  }
  return formatDuration(ms, options);
}
